/**
 * Currently use model from new repo that export proto js module by namespaces.
 * Incompatible with legacy usage.
 * https://gitlab.com/enCapital/models/-/merge_requests/83
 */

import { dnse, enbox, models, price } from '@encapital/models';
import { Wrapper } from '@encapital/krx-proto-models/proto/mdds/v1/mdds_pb';
import { TimeSync } from './timeSync';

const {
  TopPrice,
  StockInfo,
  Session,
  Tick,
  StockStatistics,
  ListStockStatistics,
  FSP,
  BidAskInfo,
  EstimatedMarketIndex,
  ListStockVolatility,
  ListSectorVolatility,
} = price;
const {
  Portfolio,
  AccountBalance,
  Order,
  MarginAccountOffer,
  SavingsAccount,
  HandoffOTPEvent,
  InvestorUpdate,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  conditional_order,
  FlexHoStatus,
  PendingDepositAndWithdrawEvent,
  PendingDepositEvent,
  SystemMaintain,
  RegistrationAnalytic,
  NotifyPaymentStatus,
  NotifyRegistrationFormStatus,
  SacoBrokerRequestEvent,
  DerivativesArenaUserRanks,
  DerivativesArenaUserRank,
  DerivativesArenaTopUsers,
  LoyaltyChangeState,
  CrossOrder,
  group_order,
  bond,
} = dnse;
const {
  Deal,
  Order: DealOrder,
  DerivativeDealWrap,
  DerivativeDealRisk,
  DerivativeCashAccount,
  DerivativeAccount,
  EstimateSettlementPrice,
  MarketSession,
} = dnse.fo;

const { Wrapper: NoticeWrapper } = dnse.notice.v1;
const { Wrapper: AccountInfoWrapper } = dnse.broker.v1;
const { EnboxNotificationBadge } = enbox;
const { MarketIndex, OHLC, EncapMessage, DerPrice, MarketBreadth, GlobalMarketIndex, GlobalOHLC } = models;
type MsgType = `${models.EncapMessage.MessageType}`;

const payloadModelMap: {
  [P in MsgType]?:
    | typeof StockInfo
    | typeof TopPrice
    | typeof Session
    | typeof Order
    | typeof AccountBalance
    | typeof Portfolio
    | typeof models.MarketIndex
    | typeof models.OHLC
    | typeof models.DerPrice
    | typeof Deal
    | typeof DealOrder
    | typeof EnboxNotificationBadge
    | typeof Tick
    | typeof MarginAccountOffer
    | typeof SavingsAccount
    | typeof HandoffOTPEvent
    | typeof InvestorUpdate
    | typeof StockStatistics
    | typeof ListStockStatistics
    | typeof FSP
    | typeof BidAskInfo
    | typeof MarketBreadth
    | typeof conditional_order.v1.Order
    | typeof EstimatedMarketIndex
    | typeof DerivativeCashAccount
    | typeof EstimatedMarketIndex
    | typeof DerivativeDealWrap
    | typeof DealOrder
    | typeof DerivativeDealRisk
    | typeof DerivativeAccount
    | typeof AccountInfoWrapper
    | typeof NoticeWrapper
    | typeof EstimateSettlementPrice
    | typeof MarketSession
    | typeof FlexHoStatus
    | typeof PendingDepositAndWithdrawEvent
    | typeof GlobalMarketIndex
    | typeof GlobalOHLC
    | typeof PendingDepositEvent
    | typeof FlexHoStatus
    | typeof SystemMaintain
    | typeof RegistrationAnalytic
    | typeof NotifyPaymentStatus
    | typeof NotifyRegistrationFormStatus
    | typeof SacoBrokerRequestEvent
    | typeof DerivativesArenaUserRanks
    | typeof DerivativesArenaUserRank
    | typeof DerivativesArenaTopUsers
    | typeof dnse.bond.v1.Portfolio
    | typeof dnse.Investor
    | typeof LoyaltyChangeState
    | typeof CrossOrder
    | typeof dnse.lbs.v1.WalletTransaction
    | typeof dnse.lbs.v1.LinkTransaction
    | typeof group_order.v1.TradingGroupOrder
    | typeof bond.v1.ListedBondOrder
    | typeof dnse.AgentDealState
    | typeof dnse.InvestorAccount
    | typeof ListSectorVolatility
    | typeof ListStockVolatility;
} = {
  [EncapMessage.MessageType.STOCK_INFO]: StockInfo,
  [EncapMessage.MessageType.TOP_PRICE]: TopPrice,
  [EncapMessage.MessageType.TRADING_SESSION]: Session,
  [EncapMessage.MessageType.DNSE_ORDER]: Order,
  [EncapMessage.MessageType.DNSE_ACCOUNT_BALANCE]: AccountBalance,
  [EncapMessage.MessageType.DNSE_PORTFOLIO]: Portfolio,
  [EncapMessage.MessageType.MARKET_INDEX]: MarketIndex,
  [EncapMessage.MessageType.OHLC]: OHLC,
  [EncapMessage.MessageType.DERIVATIVE_PRICE]: DerPrice,
  [EncapMessage.MessageType.ENBOX_BADGE]: EnboxNotificationBadge,
  [EncapMessage.MessageType.DNSE_FO_DEAL]: Deal,
  [EncapMessage.MessageType.DNSE_FO_ORDER]: DealOrder,
  [EncapMessage.MessageType.DNSE_FO_DERIVATIVE_ORDER]: DealOrder,
  [EncapMessage.MessageType.DNSE_FO_DERIVATIVE_DEAL_WRAP]: DerivativeDealWrap,
  [EncapMessage.MessageType.TICK]: Tick,
  [EncapMessage.MessageType.DNSE_MARGIN_ACCOUNT_OFFER]: MarginAccountOffer,
  [EncapMessage.MessageType.SAVINGS_ACCOUNT]: SavingsAccount,
  [EncapMessage.MessageType.DNSE_HANDOFF_SMART_OTP]: HandoffOTPEvent,
  [EncapMessage.MessageType.DNSE_HANDOFF_SMART_OTP_APPROVED]: HandoffOTPEvent,
  [EncapMessage.MessageType.DNSE_TRIGGER_INVESTOR_CHANGE]: InvestorUpdate,
  [EncapMessage.MessageType.STOCK_STATISTICS]: StockStatistics,
  [EncapMessage.MessageType.TOP_INFLUENCE_STOCKS]: ListStockStatistics,
  [EncapMessage.MessageType.LIST_STOCK_STATISTICS]: ListStockStatistics,
  [EncapMessage.MessageType.BID_ASK_INFO]: BidAskInfo,
  [EncapMessage.MessageType.MARKET_BREADTH]: MarketBreadth,
  [EncapMessage.MessageType.FSP_ESTIMATE]: FSP,
  [EncapMessage.MessageType.ESTIMATED_MARKET_INDEX]: EstimatedMarketIndex,
  [EncapMessage.MessageType.DNSE_CONDITIONAL_ORDER]: conditional_order.v1.Order,
  [EncapMessage.MessageType.GLOBAL_MARKET_INDEX]: GlobalMarketIndex,
  [EncapMessage.MessageType.GLOBAL_MARKET_OHLC]: GlobalOHLC,
  [EncapMessage.MessageType.DNSE_FO_DERIVATIVE_CASH_ACCOUNT]: DerivativeCashAccount,
  [EncapMessage.MessageType.DNSE_FO_DERIVATIVE_RISK_UPDATE]: DerivativeDealRisk,
  [EncapMessage.MessageType.DNSE_FO_DERIVATIVE_ACCOUNT]: DerivativeAccount,
  [EncapMessage.MessageType.DNSE_DERIVATIVE_ESTIMATE_SETTLEMENT_PRICE]: EstimateSettlementPrice,
  [EncapMessage.MessageType.DNSE_MARKET_SESSION]: MarketSession,
  [EncapMessage.MessageType.DNSE_ACCOUNT_CHANGE_EVENT_WRAPPER]: NoticeWrapper,
  [EncapMessage.MessageType.DNSE_BROKER_WRAPPER]: AccountInfoWrapper,
  [EncapMessage.MessageType.DNSE_FLEX_HO_STATUS]: FlexHoStatus,
  [EncapMessage.MessageType.DNSE_DERIVATIVE_PENDING_DEPOSIT_EVENT]: PendingDepositEvent,
  [EncapMessage.MessageType.DNSE_DERIVATIVE_PENDING_DEPOSIT_AND_WITHDRAW_EVENT]: PendingDepositAndWithdrawEvent,
  [EncapMessage.MessageType.DNSE_SYSTEM_MAINTAIN]: SystemMaintain,
  [EncapMessage.MessageType.DNSE_BROKER_SACO_TYPE]: SacoBrokerRequestEvent,
  [EncapMessage.MessageType.LEADERBOARD_USER_RANKS]: DerivativesArenaUserRanks,
  [EncapMessage.MessageType.LEADERBOARD_USER_RANK]: DerivativesArenaUserRank,
  [EncapMessage.MessageType.LEADERBOARD_TOP_USERS]: DerivativesArenaTopUsers,
  [EncapMessage.MessageType.DNSE_BOND_PORTFOLIO]: dnse.bond.v1.Portfolio,
  [EncapMessage.MessageType.DNSE_UPDATE_INVESTOR]: dnse.Investor,
  [EncapMessage.MessageType.LOYALTY_STATE_CHANGED]: LoyaltyChangeState,
  [EncapMessage.MessageType.DNSE_LA_WALLET_TRANSACTION]: dnse.lbs.v1.WalletTransaction,
  [EncapMessage.MessageType.DNSE_CROSS_ORDER]: CrossOrder,
  [EncapMessage.MessageType.DNSE_GROUP_ORDER]: group_order.v1.TradingGroupOrder,
  [EncapMessage.MessageType.DNSE_LA_LINK_TRANSACTION]: dnse.lbs.v1.LinkTransaction,
  [EncapMessage.MessageType.DNSE_BOND_LISTED_BOND_ORDER]: bond.v1.ListedBondOrder,
  [EncapMessage.MessageType.DNSE_SOE_DEAL]: dnse.AgentDealState,
  [EncapMessage.MessageType.DNSE_ACCOUNT_UPDATED]: dnse.InvestorAccount,
  [EncapMessage.MessageType.SECTOR_VOLATILITY]: ListSectorVolatility,
  [EncapMessage.MessageType.STOCK_VOLATILITY]: ListStockVolatility,
};

export const convertKrxMDDS = (msg, topic: string) => {
  try {
    const msgByteArray = new Uint8Array(msg);
    const msgWrapper = Wrapper.deserializeBinary(msgByteArray);
    if (msgWrapper?.hasListStockVolatility()) {
      return {
        type: Wrapper.KindCase.LIST_STOCK_VOLATILITY,
        payload: msgWrapper.getListStockVolatility()?.toObject(),
      };
    }
    if (msgWrapper?.hasListSectorVolatility()) {
      return {
        type: Wrapper.KindCase.LIST_SECTOR_VOLATILITY,
        payload: msgWrapper.getListSectorVolatility()?.toObject(),
      };
    }
    if (msgWrapper?.hasStockInfo()) {
      return {
        type: Wrapper.KindCase.STOCK_INFO,
        payload: msgWrapper.getStockInfo()?.toObject(),
      };
    }
    if (msgWrapper?.hasTopPrice()) {
      return {
        type: Wrapper.KindCase.TOP_PRICE,
        payload: msgWrapper.getTopPrice()?.toObject(),
      };
    }
    if (msgWrapper?.hasTick()) {
      return {
        type: Wrapper.KindCase.TICK,
        payload: msgWrapper.getTick()?.toObject(),
      };
    }
    if (msgWrapper.hasBoardEvent()) {
      return {
        type: Wrapper.KindCase.BOARD_EVENT,
        payload: msgWrapper.getBoardEvent()?.toObject(),
      };
    }
    if (msgWrapper.hasSymbolBoardEvent()) {
      return {
        type: Wrapper.KindCase.SYMBOL_BOARD_EVENT,
        payload: msgWrapper.getSymbolBoardEvent()?.toObject(),
      };
    }
    if (msgWrapper?.hasMarketIndex()) {
      return {
        type: Wrapper.KindCase.MARKET_INDEX,
        payload: msgWrapper.getMarketIndex()?.toObject(),
      };
    }
    return undefined;
  } catch (error) {
    console.log('An error occurs when parsing msg at', topic, error);
    return undefined;
  }
};

const timeSync = TimeSync.getInstance();

// TODO: Need type safe here, handle msg with no type better
export const EncapMsgDeserializer = {
  decodeKrx(msg, topic: string) {
    return convertKrxMDDS(msg, topic);
  },
  decode(msg: any) {
    let result;
    let message;
    try {
      const encapMsg = EncapMessage.decode(msg);
      message = encapMsg;
      const payloadDecoder = payloadModelMap[encapMsg.type];

      if (payloadDecoder) {
        const payload = payloadDecoder.toObject(payloadDecoder.decode(EncapMessage.toObject(encapMsg).payload) as any, {
          longs: Number,
          enums: String,
          defaults: true,
        });
        result = {
          type: EncapMessage.MessageType[encapMsg.type],
          payload: {
            clientReceivedTime: timeSync?.now().toISOString() ?? new Date().toISOString(),
            networkRTT: timeSync.rtt,
            ...payload,
          },
        };
      } else {
        console.log('Decoder doesnt existed. Type:', encapMsg.type);
      }
    } catch (error) {
      console.log('Decode fail:', error, msg, message);
    }

    return result;
  },
};
